import { useState, useEffect } from 'react';
import { X, Share, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { STORAGE_KEYS } from '@/lib/constants';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    const ios = isIOS();
    setIsIos(ios);

    if (!ios) {
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        try {
          const dismissed = localStorage.getItem(STORAGE_KEYS.INSTALL_PROMPT_DISMISSED);
          if (!dismissed) {
            setTimeout(() => setShowPrompt(true), 3000);
          }
        } catch (err) {
          console.error('Failed to check install prompt status:', err);
        }
      };

      window.addEventListener('beforeinstallprompt', handler);

      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
      };
    } else {
      // iOS: show after 3s if not dismissed recently
      try {
        const dismissed = localStorage.getItem(STORAGE_KEYS.INSTALL_PROMPT_DISMISSED);
        if (dismissed) {
          const dismissedAt = parseInt(dismissed, 10);
          if (Date.now() - dismissedAt < DISMISS_DURATION_MS) {
            return;
          }
        }
        setTimeout(() => setShowPrompt(true), 3000);
      } catch (err) {
        console.error('Failed to check install prompt status:', err);
      }
    }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowPrompt(false);
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    try {
      localStorage.setItem(STORAGE_KEYS.INSTALL_PROMPT_DISMISSED, Date.now().toString());
    } catch (err) {
      console.error('Failed to save install prompt dismissal:', err);
    }
  };

  if (!showPrompt) return null;

  if (isIos) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-[max(2rem,env(safe-area-inset-bottom))]">
        <Card className="p-4 bg-card shadow-lg border-2 border-primary/20">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-foreground">Install Ref Assistant</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Add to your Home Screen for quick access and offline use during games.
              </p>
              <div className="flex items-center gap-2 mb-3 p-3 bg-muted rounded-lg">
                <Share className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm text-foreground">
                  Tap the <strong>Share</strong> button in Safari, then <strong>Add to Home Screen</strong>
                </span>
              </div>
              <Button onClick={handleDismiss} size="sm" variant="outline" className="w-full">
                Got it
              </Button>
            </div>
            <button
              onClick={handleDismiss}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss install prompt"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto">
      <Card className="p-4 bg-card shadow-lg border-2 border-primary/20">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Download className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">Install Ref Assistant</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Install this app on your device for quick access and offline use during games.
            </p>
            <div className="flex gap-2">
              <Button onClick={handleInstall} size="sm" className="flex-1">
                Install
              </Button>
              <Button onClick={handleDismiss} size="sm" variant="outline">
                Not Now
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss install prompt"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </Card>
    </div>
  );
}

export default InstallPrompt;
